import { AlertTriangle } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";
import {
  EMeetingFailureStatus,
  type IMeetingFailedCardProps,
  type IResolveBodyParams,
} from "./types";

// NOTE: Render the body with a literal key per status so i18next's strict-key
// typing accepts the call. A `t(dynamicString)` would widen the key argument
// to `string` which the strict typing rejects.
const resolveBody = ({
  t,
  failureStatus,
  meetingTitle,
}: IResolveBodyParams): string => {
  switch (failureStatus) {
    case EMeetingFailureStatus.RECORDING_FAILED:
      return t("type.meeting_failed.body_recording_failed", { meetingTitle });
    case EMeetingFailureStatus.TRANSCRIPTION_FAILED:
      return t("type.meeting_failed.body_transcription_failed", {
        meetingTitle,
      });
    case EMeetingFailureStatus.SUMMARIZATION_FAILED:
      return t("type.meeting_failed.body_summarization_failed", {
        meetingTitle,
      });
    case EMeetingFailureStatus.NO_AUDIO_TRACK:
      return t("type.meeting_failed.body_no_audio_track", { meetingTitle });
    case EMeetingFailureStatus.BOT_NOT_ADMITTED:
      return t("type.meeting_failed.body_bot_not_admitted", { meetingTitle });
    case EMeetingFailureStatus.BOT_LOBBY_TIMEOUT:
      return t("type.meeting_failed.body_bot_lobby_timeout", { meetingTitle });
    default:
      return t("type.meeting_failed.body", { meetingTitle });
  }
};

const MeetingFailedCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onViewMeeting,
}: IMeetingFailedCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");

  const handleViewMeeting = useCallback(
    () => onViewMeeting(data.meetingUuid),
    [onViewMeeting, data.meetingUuid],
  );

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="meeting_failed"
      createdAt={createdAt}
      isRead={isRead}
      icon={<AlertTriangle className="h-5 w-5 text-destructive" />}
      iconBgClassName="bg-destructive/10"
      ctaLabel={
        data.isRetryable
          ? t("type.meeting_failed.retry")
          : t("type.meeting_failed.view_meeting")
      }
      onCtaClick={handleViewMeeting}
      body={resolveBody({
        t,
        failureStatus: data.failureStatus,
        meetingTitle: data.meetingTitle,
      })}
    />
  );
};

export const MeetingFailedCard = memo(MeetingFailedCardComponent);
